/*
 * Cursor-paged activity for one client or the whole practice, as a TanStack
 * infinite query kept live: the realtime stream's `activity.created` events
 * prepend to the first page, client invalidations refetch the first page
 * (client source), and actor profiles are hydrated as rows arrive. The first
 * page is persisted to the user's IndexedDB cache (see `lib/query/persister`).
 */
import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useId, useMemo, useState } from "react";

import { queryClient } from "@/lib/query/queryClient";

import { ForeignNationalsAPI } from "../../../../api/foreignNationals";
import { LawFirmActivityAPI } from "../../../../api/lawFirmActivity";
import type {
  ForeignNationalActivityLogEvent,
  ForeignNationalActivityLogPage
} from "../../../../api/types";
import {
  useRealtimeEvent,
  useRealtimeScope
} from "../../../../lib/realtime/RealtimeProvider";
import { useUserProfiles } from "../../../userProfiles/context/UserProfilesContext";
import { useOnClientInvalidate } from "../../lib/clientInvalidation";
import { filtersToQuery, type ActivityFilters } from "./activityModel";

export const ACTIVITY_PAGE_SIZE = 50;
/** How long a freshly streamed row keeps its highlight. */
export const LIVE_HIGHLIGHT_MS = 4000;

export type ActivityFeedState = {
  events: ForeignNationalActivityLogEvent[];
  /** Ids of rows that arrived over the stream in the last few seconds. */
  liveIds: ReadonlySet<string>;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  /** Re-read the first page (later pages are dropped). */
  reload: () => Promise<void>;
  loadMore: () => void;
};

type Options = {
  filters: ActivityFilters;
  pageSize?: number;
  /** Follow the source's realtime scope (off when another view already does). */
  live?: boolean;
};

/** Whose activity: one client's, or every client's in the practice. */
export type ActivitySource =
  | { kind: "client"; id: string }
  | { kind: "practice"; id: string };

type Pages = {
  pages: ForeignNationalActivityLogPage[];
  pageParams: (string | null)[];
};

const toSource = (source: string | ActivitySource): ActivitySource =>
  typeof source === "string" ? { kind: "client", id: source } : source;

const dedupe = (
  list: ForeignNationalActivityLogEvent[]
): ForeignNationalActivityLogEvent[] => {
  const seen = new Set<string>();
  return list.filter((ev) => {
    const id = String(ev?.activity_id ?? "");
    if (!id || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
};

export const activityQueryKey = (
  kind: ActivitySource["kind"],
  id: string,
  query: ReturnType<typeof filtersToQuery>,
  pageSize: number
) => ["activity", kind, id, query, pageSize] as const;

const NO_EVENTS: ForeignNationalActivityLogEvent[] = [];

const trimToFirstPage = (old: Pages | undefined): Pages | undefined =>
  old && old.pages.length > 1
    ? { pages: old.pages.slice(0, 1), pageParams: old.pageParams.slice(0, 1) }
    : old;

/**
 * Re-read the first page of every mounted feed for this source (any filter
 * set), dropping later pages. For the dashboards' "Refresh" control, which
 * sits outside the rail that owns the feed.
 */
export const reloadActivityFeeds = async (
  source: ActivitySource
): Promise<void> => {
  const queryKey = ["activity", source.kind, source.id] as const;
  queryClient.setQueriesData<Pages>({ queryKey }, trimToFirstPage);
  await queryClient.refetchQueries({ queryKey, type: "active" });
};

export const useActivityFeed = (
  sourceOrClientId: string | ActivitySource,
  { filters, pageSize = ACTIVITY_PAGE_SIZE, live = true }: Options
): ActivityFeedState => {
  const source = toSource(sourceOrClientId);
  const sourceKind = source.kind;
  const sourceId = source.id;
  const clientId = sourceKind === "client" ? sourceId : null;
  const [liveIds, setLiveIds] = useState<Set<string>>(() => new Set());
  const owner = useId();
  const { ensure: ensureProfiles } = useUserProfiles();

  // Only the server-side part of the filters drives refetches.
  const query = useMemo(() => filtersToQuery(filters), [filters]);
  const queryKey = useMemo(
    () => activityQueryKey(sourceKind, sourceId, query, pageSize),
    [pageSize, query, sourceId, sourceKind]
  );

  const feed = useInfiniteQuery(
    {
      queryKey,
      queryFn: async ({ pageParam }) => {
        const params = {
          ...query,
          limit: pageSize,
          cursor: pageParam ?? undefined
        };
        return sourceKind === "practice"
          ? LawFirmActivityAPI.listPage(params)
          : ForeignNationalsAPI.listActivityLogPage(sourceId, params);
      },
      initialPageParam: null as string | null,
      getNextPageParam: (last) => last.next_cursor ?? undefined,
      enabled: Boolean(sourceId),
      meta: { persist: true }
    },
    queryClient
  );

  const events = useMemo(
    () =>
      feed.data
        ? dedupe(feed.data.pages.flatMap((p) => p.items ?? []))
        : NO_EVENTS,
    [feed.data]
  );

  // Realtime: new rows for this source prepend with a short highlight. Each
  // mounted feed claims the scope under its own owner so two feeds (overview
  // card and tab) never release each other's.
  useRealtimeScope(
    `${sourceKind}-activity:${owner}`,
    live && sourceId ? { kind: sourceKind, scopeId: sourceId } : null
  );
  useRealtimeEvent<ForeignNationalActivityLogEvent>(
    "activity.created",
    (data) => {
      const id = String(data?.activity_id ?? "");
      if (!id) return;
      if (
        sourceKind === "client" &&
        String(data.foreign_national_id ?? "") !== sourceId
      )
        return;
      queryClient.setQueryData<Pages>(queryKey, (old) => {
        const pages = old?.pages ?? [];
        if (pages.some((p) => p.items?.some((ev) => ev.activity_id === id)))
          return old;
        const first = pages[0] ?? { items: [], next_cursor: null };
        return {
          pages: [
            { ...first, items: [data, ...(first.items ?? [])] },
            ...pages.slice(1)
          ],
          pageParams: old?.pageParams?.length ? old.pageParams : [null]
        };
      });
      setLiveIds((prev) => new Set(prev).add(id));
      window.setTimeout(() => {
        setLiveIds((prev) => {
          if (!prev.has(id)) return prev;
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }, LIVE_HIGHLIGHT_MS);
    }
  );

  const { refetch } = feed;
  const reload = useCallback(async () => {
    // Keep the first page on screen, drop the rest, then re-read page one.
    queryClient.setQueryData<Pages>(queryKey, trimToFirstPage);
    await refetch();
  }, [queryKey, refetch]);

  // Fallback when the stream is not connected: any client invalidation is a
  // reason to re-read the first page.
  useOnClientInvalidate(
    clientId,
    [
      "activity",
      "client",
      "cases",
      "documents",
      "intakes",
      "reminders",
      "records"
    ],
    () => void reload()
  );

  useEffect(() => {
    const ids = events
      .map((ev) => String(ev.actor_user_id ?? "").trim())
      .filter(Boolean);
    if (ids.length) ensureProfiles(Array.from(new Set(ids)));
  }, [ensureProfiles, events]);

  const { hasNextPage, isFetchingNextPage, isFetching, fetchNextPage } = feed;
  return {
    events,
    liveIds,
    isLoading: feed.isLoading,
    isLoadingMore: isFetchingNextPage,
    error: feed.error
      ? feed.error instanceof Error
        ? feed.error.message
        : "Failed to load activity log."
      : null,
    hasMore: feed.data ? Boolean(hasNextPage) : true,
    reload,
    loadMore: () => {
      if (!hasNextPage || isFetchingNextPage || isFetching) return;
      void fetchNextPage();
    }
  };
};
